/**
 * Subscription API client — wraps /api/subscription/* endpoints.
 * Uses Paddle for payments.
 */

import { getConverterApiBase } from "../api";
import {
  extractTransactionId,
  isHostedPaddleUrl,
  openPaddleTransactionCheckout,
} from "./paddleCheckout";

export interface SubscriptionStatus {
  email: string;
  plan: "free" | "pro";
  conversions_used_this_month: number;
  conversions_left: number | null;
  monthly_limit: number | null;
  current_period_end: string | null;
  stripe_customer_id: string | null;
}

export type McpHealthStatus = "healthy" | "degraded" | "down";

export interface HostedMcp {
  id: string;
  email: string;
  server_id: string;
  /** Slug segment for /mcp/{public_path}; UI falls back to server_id if omitted. */
  public_path?: string;
  title: string;
  base_url: string;
  tool_count: number;
  created_at: string;
  expires_at: string | null;
  is_active: number;
  is_expired: boolean;
  /** URL this MCP was originally created from — enables auto-updates. */
  source_url?: string | null;
  /** Latest proactive health probe (cron). */
  health_status?: McpHealthStatus | null;
  last_health_check?: string | null;
  last_health_error?: string | null;
  health_response_ms?: number | null;
}

export interface McpUsage {
  server_id: string;
  period_days: number;
  total_calls: number;
  errors: number;
  error_rate: number;
  avg_response_ms: number;
  top_tools: { tool: string; calls: number; errors: number }[];
  daily: { day: string; calls: number; errors: number }[];
  clients: { client: string; calls: number }[];
}

export interface UserSummary {
  plan: "free" | "pro";
  conversions_used_this_month: number;
  monthly_limit: number | null;
  conversions_left: number | null;
  active_mcps: number;
  calls_today: number;
  total_calls: number;
}

/** Bundled dashboard payload (one HTTP round-trip). */
export interface DashboardData extends SubscriptionStatus, UserSummary {
  mcps: HostedMcp[];
}

export interface ErrorLogEntry {
  id: string;
  tool: string;
  message: string;
  response_ms: number | null;
  called_at: string;
  severity: "warning" | "error";
}

export interface UptimeStats {
  server_id: string;
  period_days: number;
  total_calls: number;
  successes: number;
  failures: number;
  uptime_pct: number;
  p50_ms: number;
  p95_ms: number;
  p99_ms: number;
  error_categories: { category: string; count: number }[];
}

export interface CallLogEntry {
  id: string;
  server_id: string;
  tool_name: string;
  success: boolean;
  response_ms: number | null;
  error_message: string | null;
  request_summary: string | null;
  response_summary: string | null;
  called_at: string;
}

export interface ActivityFeed {
  items: CallLogEntry[];
  total: number;
}

const base = () => getConverterApiBase();
const FETCH_TIMEOUT_MS = 25_000;

function normEmail(email: string): string {
  return email.trim().toLowerCase();
}

async function fetchJson<T>(url: string, init?: RequestInit): Promise<T> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
  try {
    const res = await fetch(url, {
      ...init,
      signal: init?.signal ?? controller.signal,
      cache: "no-store",
    });
    if (!res.ok) {
      const detail = await res.text().catch(() => "");
      throw new Error(detail || `Request failed (${res.status})`);
    }
    return res.json() as Promise<T>;
  } catch (err) {
    if (err instanceof Error && err.name === "AbortError") {
      throw new Error("Request timed out — the server may be waking up. Try again in a moment.");
    }
    throw err;
  } finally {
    clearTimeout(timeout);
  }
}

export async function getDashboardData(email: string, init?: RequestInit): Promise<DashboardData> {
  return fetchJson<DashboardData>(
    `${base()}/api/subscription/dashboard?email=${encodeURIComponent(normEmail(email))}`,
    init,
  );
}

export async function getSubscriptionStatus(email: string): Promise<SubscriptionStatus> {
  const res = await fetch(`${base()}/api/subscription/status?email=${encodeURIComponent(normEmail(email))}`);
  if (!res.ok) throw new Error("Failed to load subscription status");
  return res.json();
}

export async function getUserMcps(email: string): Promise<HostedMcp[]> {
  const res = await fetch(`${base()}/api/subscription/mcps?email=${encodeURIComponent(normEmail(email))}`);
  if (!res.ok) throw new Error("Failed to load MCPs");
  return res.json();
}

export async function getMcpUsage(serverId: string, days = 30): Promise<McpUsage> {
  const res = await fetch(`${base()}/api/subscription/usage/${serverId}?days=${days}`);
  if (!res.ok) throw new Error("Failed to load usage");
  return res.json();
}

export async function getUserSummary(email: string, init?: RequestInit): Promise<UserSummary> {
  return fetchJson<UserSummary>(
    `${base()}/api/subscription/summary?email=${encodeURIComponent(normEmail(email))}`,
    init,
  );
}

export async function getErrorLogs(serverId: string, limit = 50, days = 7): Promise<ErrorLogEntry[]> {
  const res = await fetch(
    `${base()}/api/subscription/errors/${serverId}?limit=${limit}&days=${days}`,
  );
  if (!res.ok) throw new Error("Failed to load error logs");
  return res.json();
}

export async function getUptimeStats(serverId: string, days = 30): Promise<UptimeStats> {
  const res = await fetch(`${base()}/api/subscription/uptime/${serverId}?days=${days}`);
  if (!res.ok) throw new Error("Failed to load uptime stats");
  return res.json();
}

export async function getActivityFeed(params?: {
  serverId?: string;
  limit?: number;
  page?: number;
  status?: "success" | "error";
}): Promise<ActivityFeed> {
  const qs = new URLSearchParams();
  if (params?.serverId) qs.set("server_id", params.serverId);
  if (params?.limit) qs.set("limit", String(params.limit));
  if (params?.page) qs.set("page", String(params.page));
  if (params?.status) qs.set("status", params.status);
  const res = await fetch(`${base()}/api/v1/activity?${qs}`, { credentials: "include" });
  if (!res.ok) throw new Error("Failed to load activity feed");
  return res.json();
}

/** Manually sync plan from Paddle (fallback for webhook delays). */
export async function syncPlanFromStripe(email: string): Promise<{ plan: string; synced: boolean }> {
  const res = await fetch(`${base()}/api/subscription/sync`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });
  if (!res.ok) return { plan: "free", synced: false };
  return res.json();
}

export interface CheckoutSession {
  url: string;
  transaction_id?: string;
}

function checkoutSuccessUrl(): string {
  return `${window.location.origin}/dashboard?checkout=success`;
}

async function postCheckout(path: string, body: object): Promise<CheckoutSession> {
  const res = await fetch(`${base()}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let msg = "Failed to create checkout session";
    try {
      const err = (await res.json()) as { detail?: unknown };
      if (typeof err.detail === "string") msg = err.detail;
    } catch { /* use default */ }
    throw new Error(msg);
  }
  return res.json() as Promise<CheckoutSession>;
}

/** Create transaction and open Paddle payment (overlay or hosted URL). */
async function beginCheckout(session: CheckoutSession): Promise<void> {
  const txnId =
    session.transaction_id?.trim() ||
    extractTransactionId(session.url) ||
    null;

  if (txnId) {
    const opened = await openPaddleTransactionCheckout(txnId, {
      successUrl: checkoutSuccessUrl(),
    });
    if (opened) return;
  }

  if (session.url && isHostedPaddleUrl(session.url)) {
    window.location.href = session.url;
    return;
  }

  if (txnId) {
    window.location.href = `/pricing?_ptxn=${encodeURIComponent(txnId)}`;
    return;
  }

  throw new Error("Could not open checkout — is PADDLE_CLIENT_TOKEN configured?");
}

/** Start Paddle Pro checkout — opens payment UI (does not fake-upgrade). */
export async function startProCheckout(email: string): Promise<void> {
  const session = await postCheckout("/api/subscription/checkout", { email });
  await beginCheckout(session);
}

/** Start Paddle Pay-Per-Use checkout. */
export async function startPpuCheckout(email: string, quantity = 10): Promise<void> {
  const session = await postCheckout("/api/subscription/checkout/ppu", { email, quantity });
  await beginCheckout(session);
}

// ── MCP Versioning ────────────────────────────────────────────────────────────

export interface McpVersion {
  id: string;
  mcp_id: string;
  server_id: string;
  version: string;
  spec_hash: string;
  spec_content?: string;   // only present in single-version fetch
  tool_count: number;
  is_active: 0 | 1;
  notes: string | null;
  created_at: string;
  spec_title?: string;     // enriched by upload endpoint
}

export async function listMcpVersions(serverId: string, email: string): Promise<McpVersion[]> {
  return fetchJson<McpVersion[]>(
    `${base()}/api/subscription/versions/${serverId}?email=${encodeURIComponent(email)}`
  );
}

export async function getMcpVersion(serverId: string, versionId: string, email: string): Promise<McpVersion> {
  return fetchJson<McpVersion>(
    `${base()}/api/subscription/versions/${serverId}/${versionId}?email=${encodeURIComponent(email)}`
  );
}

export async function uploadMcpVersion(
  serverId: string,
  email: string,
  payload: { spec_content: string; notes?: string; activate?: boolean }
): Promise<McpVersion> {
  const res = await fetch(
    `${base()}/api/subscription/versions/${serverId}?email=${encodeURIComponent(email)}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  if (!res.ok) {
    let msg = "Failed to upload version";
    try {
      const err = (await res.json()) as { detail?: unknown };
      if (typeof err.detail === "string") msg = err.detail;
    } catch { /* use default */ }
    throw new Error(msg);
  }
  return res.json();
}

export async function activateMcpVersion(
  serverId: string,
  versionId: string,
  email: string
): Promise<McpVersion> {
  const res = await fetch(
    `${base()}/api/subscription/versions/${serverId}/${versionId}/activate?email=${encodeURIComponent(email)}`,
    { method: "POST" }
  );
  if (!res.ok) {
    let msg = "Failed to activate version";
    try {
      const err = (await res.json()) as { detail?: unknown };
      if (typeof err.detail === "string") msg = err.detail;
    } catch { /* use default */ }
    throw new Error(msg);
  }
  return res.json();
}

export async function deleteMcpVersion(
  serverId: string,
  versionId: string,
  email: string
): Promise<void> {
  const res = await fetch(
    `${base()}/api/subscription/versions/${serverId}/${versionId}?email=${encodeURIComponent(email)}`,
    { method: "DELETE" }
  );
  if (!res.ok) {
    let msg = "Failed to delete version";
    try {
      const err = (await res.json()) as { detail?: unknown };
      if (typeof err.detail === "string") msg = err.detail;
    } catch { /* use default */ }
    throw new Error(msg);
  }
}

// ── Webhooks ──────────────────────────────────────────────────────────────────

export const WEBHOOK_EVENTS = [
  { value: "mcp.call.completed", label: "Call completed", description: "Every successful tool call" },
  { value: "mcp.error.occurred", label: "Error occurred", description: "Any tool call that fails" },
  { value: "mcp.server.created", label: "Server created", description: "When a new MCP is hosted" },
] as const;

export type WebhookEventType = typeof WEBHOOK_EVENTS[number]["value"];

export interface Webhook {
  id: string;
  email: string;
  server_id: string | null;
  url: string;
  /** Plaintext signing secret. Only present in create / rotate responses —
   * never returned by list/get. Surface it to the user immediately and
   * discard from client state once acknowledged. */
  secret?: string;
  /** Display-safe masked form, e.g. "••••••••••••••••abcd". Always present. */
  secret_masked: string;
  events: WebhookEventType[];
  is_active: 0 | 1;
  created_at: string;
}

export interface WebhookDelivery {
  id: string;
  webhook_id: string;
  event_type: string;
  payload: Record<string, unknown>;
  status: "delivered" | "failed";
  attempts: number;
  response_status: number | null;
  error_message: string | null;
  delivered_at: string;
}

export async function listWebhooks(email: string): Promise<Webhook[]> {
  return fetchJson<Webhook[]>(
    `${base()}/api/webhooks?email=${encodeURIComponent(email)}`
  );
}

export async function createWebhook(
  email: string,
  payload: { url: string; events: string[]; server_id?: string | null }
): Promise<Webhook> {
  const res = await fetch(
    `${base()}/api/webhooks?email=${encodeURIComponent(email)}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  if (!res.ok) {
    let msg = "Failed to create webhook";
    try { const e = await res.json(); if (e.detail) msg = e.detail; } catch { /* */ }
    throw new Error(msg);
  }
  return res.json();
}

export async function deleteWebhook(webhookId: string, email: string): Promise<void> {
  const res = await fetch(
    `${base()}/api/webhooks/${webhookId}?email=${encodeURIComponent(email)}`,
    { method: "DELETE" }
  );
  if (!res.ok) throw new Error("Failed to delete webhook");
}

export async function rotateWebhookSecret(webhookId: string, email: string): Promise<Webhook> {
  const res = await fetch(
    `${base()}/api/webhooks/${webhookId}/rotate-secret?email=${encodeURIComponent(email)}`,
    { method: "POST" }
  );
  if (!res.ok) {
    let msg = "Failed to rotate secret";
    try { const e = await res.json(); if (e.detail) msg = e.detail; } catch { /* */ }
    throw new Error(msg);
  }
  return res.json();
}

export async function pingWebhook(webhookId: string, email: string): Promise<WebhookDelivery> {
  const res = await fetch(
    `${base()}/api/webhooks/${webhookId}/ping?email=${encodeURIComponent(email)}`,
    { method: "POST" }
  );
  if (!res.ok) throw new Error("Ping failed");
  return res.json();
}

export async function listWebhookDeliveries(webhookId: string, email: string): Promise<WebhookDelivery[]> {
  return fetchJson<WebhookDelivery[]>(
    `${base()}/api/webhooks/${webhookId}/deliveries?email=${encodeURIComponent(email)}`
  );
}

// ── Security ──────────────────────────────────────────────────────────────────

export interface ApiKey {
  id: string;
  email: string;
  name: string;
  key_prefix: string;
  last_used_at: string | null;
  created_at: string;
  expires_at: string | null;
  is_active: 0 | 1;
  /** Only returned on creation — never again */
  raw_key?: string;
}

export interface AuditEvent {
  id: string;
  user_id: string;
  action: string;
  resource_type: string | null;
  resource_id: string | null;
  details: Record<string, unknown> | null;
  ip_address: string | null;
  user_agent: string | null;
  created_at: number;
}

export interface IpAllowlistEntry {
  id: string;
  email: string;
  server_id: string | null;
  cidr: string;
  label: string | null;
  created_at: string;
}

export interface TotpStatus {
  enabled: boolean;
  configured: boolean;
  created_at?: string;
}

export interface TotpSetup {
  secret: string;
  uri: string;
  enabled: boolean;
}

// API Keys
export async function listApiKeys(): Promise<ApiKey[]> {
  const res = await fetch(`${base()}/api/security/api-keys`, { credentials: "include" });
  if (!res.ok) throw new Error("Failed to load API keys");
  const data = (await res.json()) as { keys: ApiKey[] };
  return data.keys;
}

export async function createApiKey(name: string, expiresInDays?: number): Promise<ApiKey> {
  const res = await fetch(`${base()}/api/security/api-keys`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name, expires_in_days: expiresInDays ?? null }),
  });
  if (!res.ok) {
    let msg = "Failed to create API key";
    try { const e = await res.json(); if (e.detail) msg = e.detail; } catch { /* */ }
    throw new Error(msg);
  }
  return res.json();
}

export async function revokeApiKey(keyId: string): Promise<void> {
  const res = await fetch(`${base()}/api/security/api-keys/${keyId}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!res.ok) throw new Error("Failed to revoke API key");
}

export async function rotateApiKey(keyId: string): Promise<ApiKey> {
  const res = await fetch(`${base()}/api/security/api-keys/${keyId}/rotate`, {
    method: "POST",
    credentials: "include",
  });
  if (!res.ok) {
    let msg = "Failed to rotate API key";
    try { const e = await res.json(); if (e.detail) msg = e.detail; } catch { /* */ }
    throw new Error(msg);
  }
  return res.json();
}

// Audit Logs
export async function getAuditLogs(params?: {
  limit?: number;
  days?: number;
  action?: string;
  resource_type?: string;
}): Promise<AuditEvent[]> {
  const qs = new URLSearchParams();
  if (params?.limit) qs.set("limit", String(params.limit));
  if (params?.days) qs.set("days", String(params.days));
  if (params?.action) qs.set("action", params.action);
  if (params?.resource_type) qs.set("resource_type", params.resource_type);
  const res = await fetch(`${base()}/api/security/audit-logs?${qs}`, { credentials: "include" });
  if (!res.ok) throw new Error("Failed to load audit logs");
  const data = (await res.json()) as { events: AuditEvent[] };
  return data.events;
}

// IP Allowlist
export async function listIpAllowlist(serverId?: string): Promise<IpAllowlistEntry[]> {
  const qs = serverId ? `?server_id=${encodeURIComponent(serverId)}` : "";
  const res = await fetch(`${base()}/api/security/ip-allowlist${qs}`, { credentials: "include" });
  if (!res.ok) throw new Error("Failed to load IP allowlist");
  const data = (await res.json()) as { entries: IpAllowlistEntry[] };
  return data.entries;
}

export async function addIpAllowlistEntry(
  cidr: string,
  label?: string,
  serverId?: string,
): Promise<IpAllowlistEntry> {
  const res = await fetch(`${base()}/api/security/ip-allowlist`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ cidr, label: label ?? null, server_id: serverId ?? null }),
  });
  if (!res.ok) {
    let msg = "Failed to add IP entry";
    try { const e = await res.json(); if (e.detail) msg = e.detail; } catch { /* */ }
    throw new Error(msg);
  }
  return res.json();
}

export async function deleteIpAllowlistEntry(entryId: string): Promise<void> {
  const res = await fetch(`${base()}/api/security/ip-allowlist/${entryId}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!res.ok) throw new Error("Failed to delete IP entry");
}

// 2FA / TOTP
export async function get2faStatus(): Promise<TotpStatus> {
  const res = await fetch(`${base()}/api/security/2fa/status`, { credentials: "include" });
  if (!res.ok) throw new Error("Failed to load 2FA status");
  return res.json();
}

export async function setup2fa(): Promise<TotpSetup> {
  const res = await fetch(`${base()}/api/security/2fa/setup`, {
    method: "POST",
    credentials: "include",
  });
  if (!res.ok) {
    let msg = "Failed to set up 2FA";
    try { const e = await res.json(); if (e.detail) msg = e.detail; } catch { /* */ }
    throw new Error(msg);
  }
  return res.json();
}

export async function verify2fa(code: string): Promise<{ ok: boolean; enabled: boolean }> {
  const res = await fetch(`${base()}/api/security/2fa/verify`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ code }),
  });
  if (!res.ok) {
    let msg = "Invalid code";
    try { const e = await res.json(); if (e.detail) msg = e.detail; } catch { /* */ }
    throw new Error(msg);
  }
  return res.json();
}

export async function disable2fa(code: string): Promise<{ ok: boolean; enabled: boolean }> {
  const res = await fetch(`${base()}/api/security/2fa/disable`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ code }),
  });
  if (!res.ok) {
    let msg = "Invalid code";
    try { const e = await res.json(); if (e.detail) msg = e.detail; } catch { /* */ }
    throw new Error(msg);
  }
  return res.json();
}

// ── Alert Preferences ─────────────────────────────────────────────────────────

export interface AlertPreferences {
  email: string;
  alert_on_down: boolean;
  alert_on_degraded: boolean;
  weekly_summary: boolean;
  slack_webhook_url: string | null;
  discord_webhook_url: string | null;
}

export async function getAlertPreferences(email: string): Promise<AlertPreferences> {
  const res = await fetch(
    `${base()}/api/subscription/alert-preferences?email=${encodeURIComponent(normEmail(email))}`,
  );
  if (!res.ok) throw new Error("Failed to load alert preferences");
  return res.json();
}

export async function updateAlertPreferences(
  email: string,
  updates: Partial<Omit<AlertPreferences, "email">>,
): Promise<AlertPreferences> {
  const res = await fetch(
    `${base()}/api/subscription/alert-preferences?email=${encodeURIComponent(normEmail(email))}`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(updates),
    },
  );
  if (!res.ok) {
    let msg = "Failed to update alert preferences";
    try { const e = await res.json(); if (e.detail) msg = e.detail; } catch { /* */ }
    throw new Error(msg);
  }
  return res.json();
}

export async function testSlackWebhook(email: string): Promise<void> {
  const res = await fetch(
    `${base()}/api/subscription/alert-preferences/test-slack?email=${encodeURIComponent(normEmail(email))}`,
    { method: "POST" },
  );
  if (!res.ok) {
    let msg = "Slack test failed";
    try { const e = await res.json(); if (e.detail) msg = e.detail; } catch { /* */ }
    throw new Error(msg);
  }
}

export async function testDiscordWebhook(email: string): Promise<void> {
  const res = await fetch(
    `${base()}/api/subscription/alert-preferences/test-discord?email=${encodeURIComponent(normEmail(email))}`,
    { method: "POST" },
  );
  if (!res.ok) {
    let msg = "Discord test failed";
    try { const e = await res.json(); if (e.detail) msg = e.detail; } catch { /* */ }
    throw new Error(msg);
  }
}

// ── Referral program ──────────────────────────────────────────────────────────

export interface ReferralStats {
  code: string;
  referral_url: string;
  total_referrals: number;
  rewarded_referrals: number;
  bonus_conversions: number;
}

export async function getReferralCode(email: string): Promise<{ code: string; referral_url: string }> {
  const res = await fetch(
    `${base()}/api/referral/my-code?email=${encodeURIComponent(normEmail(email))}`,
  );
  if (!res.ok) throw new Error("Failed to load referral code");
  return res.json();
}

export async function getReferralStats(email: string): Promise<ReferralStats> {
  const [codeRes, statsRes] = await Promise.all([
    fetch(`${base()}/api/referral/my-code?email=${encodeURIComponent(normEmail(email))}`),
    fetch(`${base()}/api/referral/stats?email=${encodeURIComponent(normEmail(email))}`),
  ]);
  if (!codeRes.ok || !statsRes.ok) throw new Error("Failed to load referral stats");
  const [codeData, statsData] = await Promise.all([codeRes.json(), statsRes.json()]);
  return { ...codeData, ...statsData };
}

export async function claimReferral(email: string, refCode: string): Promise<{ claimed: boolean }> {
  const res = await fetch(
    `${base()}/api/referral/claim?email=${encodeURIComponent(normEmail(email))}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ref_code: refCode }),
    },
  );
  if (!res.ok) return { claimed: false };
  return res.json();
}

export interface ShareToken {
  token: string;
  server_id: string;
  label: string | null;
  created_at: string;
}

export async function createShareToken(serverId: string, email: string, label?: string): Promise<ShareToken> {
  const res = await fetch(
    `${base()}/api/subscription/mcps/${serverId}/share?email=${encodeURIComponent(normEmail(email))}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ label: label ?? null }),
    },
  );
  if (!res.ok) throw new Error("Failed to create share link");
  return res.json();
}

export async function listShareTokens(serverId: string, email: string): Promise<ShareToken[]> {
  const res = await fetch(
    `${base()}/api/subscription/mcps/${serverId}/share?email=${encodeURIComponent(normEmail(email))}`,
  );
  if (!res.ok) throw new Error("Failed to load share links");
  return res.json();
}

export async function revokeShareToken(token: string, email: string): Promise<void> {
  const res = await fetch(
    `${base()}/api/subscription/share/${token}?email=${encodeURIComponent(normEmail(email))}`,
    { method: "DELETE" },
  );
  if (!res.ok) throw new Error("Failed to revoke share link");
}

// ── Probe-based Uptime Stats ──────────────────────────────────────────────────

export interface ProbeUptimeStats {
  server_id: string;
  period_days: number;
  total_probes: number;
  healthy_probes: number;
  degraded_probes: number;
  down_probes: number;
  uptime_pct: number;
  p50_ms: number | null;
  p95_ms: number | null;
  p99_ms: number | null;
  incident_count: number;
  hourly: { hour: string; status: string; response_time_ms: number | null }[];
}

export async function getProbeUptimeStats(serverId: string, days = 30): Promise<ProbeUptimeStats> {
  const res = await fetch(
    `${base()}/api/subscription/probe-uptime/${serverId}?days=${days}`,
  );
  if (!res.ok) throw new Error("Failed to load probe uptime stats");
  return res.json();
}

export interface HealthCheckRow {
  id: string;
  mcp_id: string;
  checked_at: number;
  status: "healthy" | "degraded" | "down";
  response_time_ms: number | null;
  error_message: string | null;
  http_status: number | null;
}

export async function getMcpHealthHistory(serverId: string, limit = 48): Promise<HealthCheckRow[]> {
  const res = await fetch(
    `${base()}/api/subscription/health/${serverId}/history?limit=${limit}`,
  );
  if (!res.ok) throw new Error("Failed to load health history");
  return res.json();
}
